import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Plus, Trash2, Edit2, Mail, MessageSquare, Bell, Clock, Calendar } from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import TechDialog from "@/components/TechDialog";
import { CapabilityAuditBanner } from "@/components/TechCapabilityWarning";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Backend uses ISO standard: 0=Monday, 1=Tuesday, ..., 6=Sunday
const ISO_DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Convert 24-hour time (HH:MM) to 12-hour format (h:MM AM/PM)
const formatTime12Hour = (time24) => {
  if (!time24) return "";
  const [hours, minutes] = time24.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${hours12}:${minutes.toString().padStart(2, "0")} ${period}`;
};

export default function TechManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [techs, setTechs] = useState([]);
  const [techAvailability, setTechAvailability] = useState({});
  const [showTechDialog, setShowTechDialog] = useState(false);
  const [editingTech, setEditingTech] = useState(null);
  const [deleteTechId, setDeleteTechId] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('session_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, { 
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAuthenticated(true);
          setUser(response.data);
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTechs();
      fetchAllAvailability();
    }
  }, [isAuthenticated]);

  const fetchTechs = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/techs`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTechs(response.data);
    } catch (error) {
      toast.error("Failed to fetch techs");
    }
  };

  const fetchAllAvailability = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/availability`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      // Group availability by tech_id
      const grouped = {};
      response.data.forEach(avail => {
        if (!grouped[avail.tech_id]) {
          grouped[avail.tech_id] = [];
        }
        grouped[avail.tech_id].push(avail);
      });
      setTechAvailability(grouped);
    } catch (error) {
      console.error("Failed to fetch availability:", error);
    }
  };

  const formatAvailability = (techId) => {
    const avails = techAvailability[techId] || [];
    if (avails.length === 0) return null;
    
    // Sort by day_of_week (0=Monday in backend/ISO standard)
    avails.sort((a, b) => a.day_of_week - b.day_of_week);
    
    return avails.map(a => ({
      day: ISO_DAY_NAMES[a.day_of_week], // Use ISO day names (0=Mon, 6=Sun)
      start: a.start_time,
      end: a.end_time
    }));
  };

  const handleDeleteTech = async () => {
    if (!deleteTechId) return;

    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/techs/${deleteTechId}`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Tech deleted");
      setDeleteTechId(null);
      fetchTechs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete tech");
    }
  };

  const handleEditTech = (tech) => {
    setEditingTech(tech);
    setShowTechDialog(true);
  };

  const handleDialogClose = (open) => {
    if (!open) {
      setEditingTech(null);
    }
    setShowTechDialog(open);
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-zinc-200 border-t-[#207024]"></div>
      </div>
    );
  }

  if (user?.role !== "Admin") {
    return (
      <div className="min-h-screen bg-white">
        <Header user={user} />
        <div className="max-w-md mx-auto pb-24 p-4">
          <div className="text-center py-12">
            <p className="font-inter text-zinc-500">Only admins can manage techs</p>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header user={user} />
      
      <div className="max-w-md mx-auto pb-24 p-4">
        <div className="mb-6" data-testid="techs-header">
          <h1 className="font-barlow text-4xl font-bold text-zinc-950 uppercase mb-2">Screen Techs</h1>
          <p className="font-inter text-zinc-600">Manage employee access & notifications</p>
        </div>

        <CapabilityAuditBanner />

        <div className="space-y-4" data-testid="techs-list">
          {techs.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-inter text-zinc-500">No techs found</p>
            </div>
          ) : (
            techs.map((tech) => (
              <div
                key={tech.tech_id}
                data-testid={`tech-card-${tech.tech_id}`}
                className="bg-white rounded-xl border-2 border-zinc-100 shadow-sm p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-inter text-lg font-bold text-zinc-900" data-testid="tech-name">
                      {tech.name}
                    </h3>
                    <p className="text-sm text-zinc-600" data-testid="tech-email">{tech.email}</p>
                    {tech.phone && (
                      <p className="text-sm text-zinc-500">{tech.phone}</p>
                    )}
                    <span className={`inline-block mt-2 px-2 py-1 text-xs font-semibold rounded ${
                      tech.role === "Admin" 
                        ? "bg-purple-100 text-purple-700" 
                        : "bg-[#207024]/10 text-[#207024]"
                    }`} data-testid="tech-role">
                      {tech.role}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditTech(tech)}
                      data-testid="edit-tech-button"
                      className="h-10 w-10 rounded-lg bg-zinc-100 border-2 border-zinc-200 text-zinc-700 hover:bg-zinc-200 flex items-center justify-center"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteTechId(tech.tech_id)}
                      data-testid="delete-tech-button"
                      className="h-10 w-10 rounded-lg bg-red-50 border-2 border-red-200 text-red-700 hover:bg-red-100 flex items-center justify-center"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Notification Status */}
                <div className="flex flex-wrap gap-2 pt-3 border-t border-zinc-100">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                    tech.notify_sms !== false ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-400"
                  }`}>
                    <MessageSquare className="w-3 h-3" />
                    SMS {tech.notify_sms !== false ? "On" : "Off"}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                    tech.notify_email !== false ? "bg-blue-100 text-blue-700" : "bg-zinc-100 text-zinc-400"
                  }`}>
                    <Mail className="w-3 h-3" />
                    Email {tech.notify_email !== false ? "On" : "Off"}
                  </span>
                  {tech.role === "Admin" && tech.notify_new_estimates && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-700">
                      <Bell className="w-3 h-3" />
                      Estimate Alerts
                    </span>
                  )}
                </div>

                {/* Availability Schedule */}
                {formatAvailability(tech.tech_id) && (
                  <div className="mt-3 pt-3 border-t border-zinc-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-[#207024]" />
                      <span className="text-xs font-semibold text-zinc-700 uppercase">Schedule</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {formatAvailability(tech.tech_id).map((slot, idx) => (
                        <span 
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-zinc-100 text-zinc-600"
                        >
                          <span className="font-semibold">{slot.day}</span>
                          <span className="text-zinc-400">{formatTime12Hour(slot.start)} - {formatTime12Hour(slot.end)}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <button
        onClick={() => {
          setEditingTech(null);
          setShowTechDialog(true);
        }}
        data-testid="add-tech-button"
        className="fixed bottom-24 right-4 md:right-[calc(50%-12rem)] h-16 w-16 rounded-full bg-[#207024] hover:bg-[#1a5c1d] text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
      >
        <Plus className="w-8 h-8" />
      </button>

      <TechDialog
        open={showTechDialog}
        onOpenChange={handleDialogClose}
        editTech={editingTech}
        onSuccess={() => {
          fetchTechs();
          setShowTechDialog(false);
          setEditingTech(null);
        }}
      />

      <AlertDialog open={!!deleteTechId} onOpenChange={(open) => !open && setDeleteTechId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tech</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this tech? They will no longer be able to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-tech">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTech} data-testid="confirm-delete-tech" className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
}
